import * as React from "react";

import { Divider, Drawer, Typography } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PetsIcon from "@mui/icons-material/Pets";
import BusinessIcon from "@mui/icons-material/Business";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Box from "@mui/material/Box";
import StyleIcon from "@mui/icons-material/Style";
import { userSelector } from "reduxStore/auth/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { ROLES } from "types/user";
import { DrawerHeader, StyledImg } from "components/Header/styles";
import { LogoWrapper, SidebarStyled } from "./styles";
import { LAYOUT } from "helpers/constants";
import { MenuList, OrganizationNav } from "./components";
import {
  EditNote,
  Feedback,
  Home,
  InsertDriveFile,
  LocationOn,
  School,
  Spa,
  Assignment,
  Badge as BadgeIcon,
  Campaign as CampaignIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  Storefront as StorefrontIcon,
  Forum as ForumIcon,
} from "@mui/icons-material";
import { AdminRoutes, CoachRoutes, StudentRoutes } from "config/routes";
import { useChatUnread } from "context/ChatUnreadContext";
import { useNotificationUnreadCount } from "hooks/useNotificationUnreadCount";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { useQuery } from "@apollo/client";
import { GET_USER_BADGES } from "graphql/badge/badge.query";
import { BadgeDisplay } from "components/Badge/BadgeDisplay";
import { BadgeAnimationModal } from "components/Badge/BadgeAnimationModal";
import { Badge } from "types/badge";

type SidebarProps = {
  menuIsOpen: boolean;
  onClose: () => void;
};
export const Sidebar: React.FC<SidebarProps> = ({ menuIsOpen, onClose }) => {
  const user = useAppSelector(userSelector);
  const isAdmin = React.useMemo(() => user?.role === ROLES.ADMIN, [user]);
  const organization = useAppSelector(organizationSelectedSelector);
  const { data: userBadgesData } = useQuery(GET_USER_BADGES, {
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id,
  });
  const [selectedBadges, setSelectedBadges] = React.useState<Badge[]>([]);
  const [showBadgeModal, setShowBadgeModal] = React.useState(false);
  const menuList = React.useMemo(
    () => [
      {
        title: "Home",
        icon: <Home sx={{ color: "black" }} />,
        path: AdminRoutes.Home,
        roles: [ROLES.ADMIN, ROLES.COACH, ROLES.STUDENT, ROLES.MANAGER, ROLES.DIRECTOR],
      },
      {
        title: "Chat",
        icon: <ChatIcon sx={{ color: "black" }} />,
        path: AdminRoutes.Chat,
        roles: [
          ROLES.COACH,
          ROLES.STUDENT,
          ROLES.MANAGER,
          ROLES.DIRECTOR,
        ],
      },
      {
        title: "Vet Discussions",
        icon: <ForumIcon sx={{ color: "black" }} />,
        path: AdminRoutes.Posts,
        roles: [
          ROLES.ADMIN,
          ROLES.COACH,
          ROLES.MANAGER,
          ROLES.DIRECTOR,
          ROLES.STUDENT,
        ],
      },
      {
        title: "Requirements",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        path: StudentRoutes.Requirements,
        roles: [ROLES.STUDENT, ROLES.COACH],
      },
      {
        title: "Metrics",
        icon: <AnalyticsIcon sx={{ color: "black" }} />,
        path: AdminRoutes.Metrics,
        roles: [ROLES.ADMIN, ROLES.DIRECTOR, ROLES.MANAGER],
      },
      {
        title: "Mentors",
        icon: <PeopleIcon sx={{ color: "black" }} />,
        path: StudentRoutes.MentorList,
        roles: [ROLES.STUDENT],
      },
      {
        title: "VetStore",
        icon: <StorefrontIcon sx={{ color: "black" }} />,
        path: AdminRoutes.VetStore,
        roles: [
          ROLES.ADMIN,
          ROLES.COACH,
          ROLES.STUDENT,
          ROLES.MANAGER,
          ROLES.DIRECTOR,
        ],
      },
      {
        title: "Learners",
        icon: <PeopleIcon sx={{ color: "black" }} />,
        path: CoachRoutes.Learners,
        roles: [ROLES.COACH],
      },
      {
        title: "Organization",
        icon: <BusinessIcon sx={{ color: "black" }} />,
        path: AdminRoutes.OrganizationList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Resources",
        icon: <UploadFileIcon sx={{ color: "black" }} />,
        path: AdminRoutes.ResourcesList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Wellness Resources",
        icon: <Spa sx={{ color: "black" }} />,
        path: AdminRoutes.WellnessResourcesList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Tags",
        icon: <StyleIcon sx={{ color: "black" }} />,
        path: AdminRoutes.TagsList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Badges",
        icon: <BadgeIcon sx={{ color: "black" }} />,
        path: AdminRoutes.BadgesList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Users",
        icon: <PeopleIcon sx={{ color: "black" }} />,
        path: AdminRoutes.User,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Program Templates",
        icon: <School sx={{ color: "black" }} />,
        path: AdminRoutes.AcademicYearList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Requirements",
        icon: <InsertDriveFile sx={{ color: "black" }} />,
        roles: [ROLES.ADMIN],
        children: [
          {
            title: "EPA",
            icon: <InsertDriveFile sx={{ color: "black" }} />,
            path: AdminRoutes.EPAList,
            roles: [ROLES.ADMIN],
          },
          {
            title: "Competencies",
            icon: <InsertDriveFile sx={{ color: "black" }} />,
            path: AdminRoutes.CompetenciesList,
            roles: [ROLES.ADMIN],
          },
          {
            title: "Domains",
            icon: <InsertDriveFile sx={{ color: "black" }} />,
            path: AdminRoutes.DomainsList,
            roles: [ROLES.ADMIN],
          },
          {
            title: "ITER",
            icon: <InsertDriveFile sx={{ color: "black" }} />,
            path: AdminRoutes.ITERsList,
            roles: [ROLES.ADMIN],
          },
        ],
      },
      {
        title: "Species",
        icon: <PetsIcon sx={{ color: "black" }} />,
        path: AdminRoutes.SpeciesList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Sites",
        icon: <LocationOn sx={{ color: "black" }} />,
        path: AdminRoutes.SitesList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Surveys",
        icon: <Assignment sx={{ color: "black" }} />,
        path: AdminRoutes.SurveysList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Campaigns",
        icon: <CampaignIcon sx={{ color: "black" }} />,
        path: AdminRoutes.CampaignsList,
        roles: [ROLES.ADMIN],
      },
      {
        title: "Feedback",
        icon: <EditNote sx={{ color: "black" }} />,
        path: CoachRoutes.FeedbackPortal,
        roles: [ROLES.COACH],
      },
      {
        title: "Feedback",
        icon: <EditNote sx={{ color: "black" }} />,
        path: StudentRoutes.FeedbackPortal,
        roles: [ROLES.STUDENT],
      },
      {
        title: "Wellness",
        icon: <Spa sx={{ color: "black" }} />,
        path: StudentRoutes.WellnessDEIB,
        roles: [ROLES.STUDENT],
      },
      {
        title: "Resources",
        icon: <UploadFileIcon sx={{ color: "black" }} />,
        path: AdminRoutes.ResourcesList,
        roles: [ROLES.STUDENT, ROLES.COACH],
      },
      {
        title: "Feedback",
        icon: <Feedback sx={{ color: "black" }} />,
        path: AdminRoutes.FeedbackList,
        roles: [ROLES.ADMIN],
      },
    ],
    [organization],
  );

  // const getMenu = () => {
  //   return menuList().filter((item) =>
  //     user?.role ? item.roles?.includes(user?.role) : false,
  //   );
  // };

  // console.log("menuList", menuList);
  const { totalUnread } = useChatUnread();
  const { unreadCount: notificationUnread } = useNotificationUnreadCount();

  const menu = React.useMemo(() => {
    return menuList
      .filter((item) =>
        user?.role ? item.roles?.includes(user?.role) : false,
      )
      .map((item) => {
        if (item.path === AdminRoutes.Chat) {
          return { ...item, badgeCount: totalUnread };
        }
        if (item.path === AdminRoutes.Notifications) {
          return { ...item, badgeCount: notificationUnread };
        }
        return item;
      });
  }, [menuList, user, totalUnread, notificationUnread]);

  // console.log("~~~menu", menu);

  return (
    <>
      {/* mobile */}

      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1100,
          display: { xs: "block", md: "none" },
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          opacity: menuIsOpen ? 1 : 0,
          visibility: menuIsOpen ? "visible" : "hidden",
          pointerEvents: menuIsOpen ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
        onClick={onClose}
      />
      <Drawer
        sx={{
          width: LAYOUT.DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: LAYOUT.DRAWER_WIDTH,
            boxSizing: "border-box",
            paddingTop: "env(safe-area-inset-top, 0px)",
          },
          display: { xs: "flex", md: "none" },
        }}
        variant="persistent"
        anchor="left"
        open={menuIsOpen}
        onClose={onClose}
      >
        <Divider />
        <DrawerHeader>
          <Box sx={{ flexGrow: 0 }}>
            <StyledImg src="/images/logo.png" alt="logo image" />
          </Box>
        </DrawerHeader>
        {/* User info for mobile */}
        {user && (
          <Box px={2} py={1.5} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
            {userBadgesData?.user_badge && userBadgesData.user_badge.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}>
                <BadgeDisplay
                  badges={userBadgesData.user_badge as any}
                  size="small"
                  onBadgeClick={(badge) => {
                    setSelectedBadges([badge]);
                    setShowBadgeModal(true);
                  }}
                />
              </Box>
            )}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {user.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block", mb: 0.5 }}>
              {user.email}
            </Typography>
            {user.role && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", display: "block" }}>
                Role: <strong>{user.role.toUpperCase()}</strong>
              </Typography>
            )}
          </Box>
        )}
        <MenuList listItems={menu} onClickEvent={onClose} />
        {isAdmin && (
          <>
            <Box px={1} sx={{ fontWeight: "600" }}>
              Organization View
            </Box>
          </>
        )}
        {user && (
          <Box mt={2}>
            <OrganizationNav user={user} onClickEvent={onClose} />
          </Box>
        )}
      </Drawer>

      {/* desktop */}
      <SidebarStyled sx={{ display: { xs: "none", md: "flex" } }}>
        <Box px={1} sx={{ overflow: "scroll" }}>
          <LogoWrapper>
            <StyledImg src="/images/logo.png" alt="logo image" />
          </LogoWrapper>
          <MenuList listItems={menu} />

          {isAdmin && (
            <>
              <Divider sx={{ my: 1 }} />
            </>
          )}
          {user && (
            <Box mt={2}>
              <OrganizationNav user={user} />
            </Box>
          )}
        </Box>
      </SidebarStyled>

      <BadgeAnimationModal
        badges={selectedBadges}
        open={showBadgeModal}
        onClose={() => {
          setShowBadgeModal(false);
          setSelectedBadges([]);
        }}
      />
    </>
  );
};
